import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Chart as ChartJS, registerables } from 'chart.js';
import { Line } from 'react-chartjs-2';
import clsx from 'clsx';
import { useRef } from 'react';
import { getCurrentPeriodTransactionStats } from 'features/influencer/actions';
import { useParams } from 'react-router-dom';
import { useAppContext } from 'components/Context/AppContext';
import { pdf } from '@react-pdf/renderer';
import Invoice from './invoice';

ChartJS.register(...registerables);

const InfluencerMetricsChart = () => {
  const dispatch = useDispatch();
  const { showToast } = useAppContext();
  const { influencerId } = useParams();
  const { byId } = useSelector((state) => state.influencer);

  const startDateFormatted = formatDate(byId?.created_at);
  const endDateFormatted = formatDate(byId?.influencer_expired_at);
  const startDateYMD = formatToYMD(byId?.created_at);
  const endDateYMD = formatToYMD(byId?.influencer_expired_at);
  const chartRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('total_redeem');
  const [stats, setStats] = useState({
    total_usage: null,
    total_redeem: null,
    total_click: null,
  });

  const [rawStats, setRawStats] = useState({
    total_usage: [],
    total_redeem: [],
    total_click: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const types = ['total_usage', 'total_redeem', 'total_click'];

        const results = await Promise.all(
          types.map((type) => dispatch(getCurrentPeriodTransactionStats({ id: influencerId, type })).unwrap()),
        );

        const normalizedResults = {
          total_usage: normalizeData(results[0], startDateYMD, endDateYMD),
          total_redeem: normalizeData(results[1], startDateYMD, endDateYMD),
          total_click: normalizeData(results[2], startDateYMD, endDateYMD),
        };

        setRawStats({
          total_usage: results[0],
          total_redeem: results[1],
          total_click: results[2],
        });

        setStats(normalizedResults);
        // setStats({
        //   total_usage: results[0],
        //   total_redeem: results[1],
        //   total_click: results[2],
        // });
      } catch (error) {
        showToast({ type: 'error', message: 'Gagal Mendapatkan Data Transaksi' });
      } finally {
        setIsLoading(false);
      }
    };

    if (influencerId) {
      fetchStats();
    }
  }, [influencerId, dispatch]);

  const allNull = Object.values(stats).every((value) => value === null);

  if (isLoading) {
    return (
      <div className="card-dashboard-item-skeleton">
        <div className="card-dashboard-item-skeleton__header separator">
          <div className="title-skeleton" />
          <div className="filter-skeleton" />
        </div>

        <div className="card-dashboard-item-skeleton__value" style={{ width: '100%', height: '200px' }} />
      </div>
    );
  }

  if (allNull) {
    return (
      <div className="error-state">
        <p>Data grafik tidak ditemukan.</p>
      </div>
    );
  }

  const label = {
    total_redeem: 'Jumlah Redeem',
    total_click: 'Jumlah Click',
    total_usage: 'Jumlah Penggunaan',
  };

  const data = {
    labels: stats[activeTab]?.map((d) => new Date(d.label).getDate().toString()),
    datasets: [
      {
        label: label[activeTab],
        data: stats[activeTab]?.map((d) => d.value),
        pointBorderWidth: 0,
        lineTension: 0.4,
        borderWidth: 5.79,
        borderCapStyle: 'round',
        borderColor: function (context) {
          const { ctx } = context.chart;

          const gradientStroke = ctx.createLinearGradient(500, 0, 100, 0);
          gradientStroke.addColorStop(1, '#bbe6ff');
          gradientStroke.addColorStop(0, '#009EF7');

          ctx.strokeStyle = gradientStroke;
        },
      },
    ],
  };

  const maxY = Math.max(...data.datasets[0].data);
  const stepSize = getNiceStepSize(maxY, 4); // aim for 5 ticks
  const suggestedMax = Math.ceil(maxY / stepSize) * stepSize;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: 700,
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
        max: maxY,
        // min: 0,
        beginAtZero: true,
        suggestedMax,
        ticks: {
          stepSize,
          callback: (value) => value.toLocaleString('id-ID'),
          font: {
            size: 12,
            weight: 700,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  const exportPDF = async () => {
    try {
      setIsExporting(true); // 👉 Start loading

      const chartTypes = ['total_usage', 'total_redeem', 'total_click'];
      const chartImages = [];

      const originalTab = activeTab;

      for (const type of chartTypes) {
        setActiveTab(type);

        // Wait for chart to re-render
        await new Promise((resolve) => setTimeout(resolve, 300));

        if (!chartRef.current) continue;

        const base64Image = chartRef.current.toBase64Image();
        const chartData = rawStats[type];

        chartImages.push({
          image: base64Image,
          type,
          data: chartData,
        });
      }

      setActiveTab(originalTab); // restore tab

      const blob = await pdf(
        <Invoice
          influencerName={byId.name ?? ''}
          charts={chartImages}
          period={{ startDate: startDateFormatted, endDate: endDateFormatted }}
        />,
      ).toBlob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${byId.name}-${startDateFormatted}_to_${endDateFormatted}.pdf`;
      link.click();
    } catch (error) {
      showToast({ type: 'error', message: 'Gagal export PDF' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="card-dashboard-item">
      <div className="card-dashboard-item__header separator influencer-metrics">
        <h2 className="title bold">Dashboard Influencer</h2>
        <span>({`Periode ${startDateFormatted} - ${endDateFormatted}`})</span>

        <div className="dashboard-controls">
          <button
            className={clsx('control-btn', activeTab === 'total_usage' && !isExporting && 'active')}
            onClick={() => setActiveTab('total_usage')}
            disabled={isExporting}
          >
            Jumlah Penggunaan
          </button>
          <button
            className={clsx('control-btn', activeTab === 'total_redeem' && !isExporting && 'active')}
            onClick={() => setActiveTab('total_redeem')}
            disabled={isExporting}
          >
            Jumlah Redeem
          </button>
          <button
            className={clsx('control-btn', activeTab === 'total_click' && !isExporting && 'active')}
            onClick={() => setActiveTab('total_click')}
            disabled={isExporting}
          >
            Jumlah Click
          </button>
          <button className="control-btn export" onClick={exportPDF} disabled={isExporting}>
            {isExporting ? 'Loading...' : 'Export'}
          </button>
        </div>
      </div>

      <div className="card-dashboard-item__content" style={{ height: '200px' }}>
        {rawStats[activeTab].length === 0 ? (
          <div className="empty-state">
            <p>Data tidak ditemukan.</p>
          </div>
        ) : (
          <Line ref={chartRef} options={options} data={data} />
        )}
      </div>
    </div>
  );
};

export default React.memo(InfluencerMetricsChart);

// Helper: Normalize API data to full month
function normalizeData(data, startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const map = new Map(data.map((d) => [d.label, d.value]));
  const result = [];

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split('T')[0]; // format YYYY-MM-DD
    result.push({
      label: key,
      value: map.get(key) ?? 0,
    });
  }

  return result;
}

// function getCurrentMonthRange() {
//   const now = new Date();

//   const start = new Date(now.getFullYear(), now.getMonth(), 1);
//   const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

//   const format = (date) => {
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
//   };

//   return {
//     startDate: format(start),
//     endDate: format(end),
//   };
// }

// Format to DD/MM/YY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

function formatToYMD(dateString) {
  if (!dateString) return;

  const date = new Date(dateString);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD
}

export function getNiceStepSize(maxValue, tickCount = 4) {
  const roughStep = maxValue / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep))); // base unit
  const normalized = roughStep / magnitude;

  let niceNormalized;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;

  return niceNormalized * magnitude;
}
